import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Menu, Phone, X } from "lucide-react";
import { companyInfo, navLinks } from "../../data/mock";

const API_BASE_URL = "https://teamamtbsolution.com/api";
const LOCATION_CACHE_KEY = "visitor_location";
const LOCATION_PROMPTED_KEY = "visitor_location_prompted";
const GOOGLE_PROMPTED_KEY = "visitor_google_prompted";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /* ================= SCROLL ================= */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= VISITOR TRACK ================= */
  useEffect(() => {
    const sendVisitorTrack = (locationData = null) => {
      fetch(`${API_BASE_URL}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: location.pathname,
          email: null,
          browserEmailStatus:
            "Browser Google account email is not accessible without Google Sign-In consent.",
          location: locationData,
        }),
      }).catch(() => {});
    };

    const cachedLocation = localStorage.getItem(LOCATION_CACHE_KEY);

    if (cachedLocation) {
      try {
        sendVisitorTrack(JSON.parse(cachedLocation));
        return;
      } catch {
        localStorage.removeItem(LOCATION_CACHE_KEY);
      }
    }

    sendVisitorTrack();

    if (
      localStorage.getItem(LOCATION_PROMPTED_KEY) ||
      !("geolocation" in navigator)
    ) {
      return;
    }

    const requestTimer = setTimeout(() => {
      localStorage.setItem(LOCATION_PROMPTED_KEY, "true");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            capturedAt: new Date().toISOString(),
          };

          localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(locationData));
          sendVisitorTrack(locationData);
        },
        () => {},
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 10 * 60 * 1000,
        }
      );
    }, 1500);

    return () => clearTimeout(requestTimer);
  }, [location.pathname]);

  /* ================= GOOGLE EMAIL CONSENT ================= */
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || localStorage.getItem(GOOGLE_PROMPTED_KEY)) {
      return;
    }

    const sendGoogleCredential = (credential) => {
      let cachedLocation = null;

      try {
        cachedLocation = JSON.parse(localStorage.getItem(LOCATION_CACHE_KEY));
      } catch {
        cachedLocation = null;
      }

      fetch(`${API_BASE_URL}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: location.pathname,
          googleCredential: credential,
          browserEmailStatus:
            "Visitor shared Google email through Google Sign-In consent.",
          location: cachedLocation,
        }),
      }).catch(() => {});
    };

    const initializeGooglePrompt = () => {
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          if (response.credential) {
            localStorage.setItem(GOOGLE_PROMPTED_KEY, "true");
            sendGoogleCredential(response.credential);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.prompt((notification) => {
        if (
          notification.isDismissedMoment?.() ||
          notification.isSkippedMoment?.()
        ) {
          localStorage.setItem(GOOGLE_PROMPTED_KEY, "true");
        }
      });
    };

    if (window.google?.accounts?.id) {
      initializeGooglePrompt();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGooglePrompt;
    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [location.pathname]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-[#1a1a2e] to-[#16213e] text-white py-2 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 hover:text-[#00d4ff] transition-colors"
            >
              <Phone size={14} />
              <span>{companyInfo.phone}</span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 hover:text-[#00d4ff] transition-colors"
            >
              <Mail size={14} />
              <span>{companyInfo.email}</span>
            </a>
          </div>
          <div className="text-gray-300">{companyInfo.address}</div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-white shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                className="h-14 w-auto object-contain transition-transform group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-[#B8336A] via-[#2D6A6A] to-[#3B82F6] bg-clip-text text-transparent">
                  {companyInfo.name}
                </h1>
                <p className="text-xs text-gray-500">{companyInfo.tagline}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                    location.pathname === link.path
                      ? "text-[#B8336A]"
                      : "text-gray-700 hover:text-[#B8336A]"
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] transition-all duration-300 ${
                      location.pathname === link.path
                        ? "w-3/4"
                        : "w-0 group-hover:w-3/4"
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="px-6 py-3 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-medium text-sm hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                Get In Touch
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? "max-h-screen" : "max-h-0"
          }`}
        >
          <div className="px-4 py-4 bg-white border-t space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? "bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-[#B8336A]"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-lg text-center font-medium"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
